import { useState } from 'react'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { OwnCvFileInput, OwnCvStatusPanel } from '@/components/files/own-cv-replace'
import { useOwnCvReplace } from '@/hooks/use-own-cv-replace'
import { API_ORIGIN, getPageHeader } from '@/constants'
import { getPluginSlots } from '@/plugin/slots'
import { authorizedFetch } from '@/lib/api'
import { PageContainer } from '@/components/ui/page-container'
import { PageHeader } from '@/components/ui/page-header'
import { PageHeaderLeading } from '@/components/navigation/page-header-leading'
import { Card, CardContent, CardDescription, CardHeader } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { FileUpload } from '@/components/ui/file-upload'
import { Progress } from '@/components/ui/progress'
import { FileListSkeleton } from '@/components/layout/file-row-skeleton'
import { Badge } from '@/components/ui/badge'
import { FileText, Loader2, Trash2 } from 'lucide-react'

/** Align with `THREAD_ATTACHMENT_MAX_BYTES` default in the API (10 MiB). */
const PER_FILE_MAX_BYTES = 10 * 1024 * 1024

interface FileAssetRow {
  id: string
  original_name: string
  mime_type: string
  size_bytes: number
  updated_at?: string
  created_at?: string
  file_url?: string
  purpose?: string
}

interface PendingUploadRow {
  id: string
  original_name: string
  size_bytes: number
  status: 'uploading' | 'failed'
  error?: string
}

interface MyFilesLibraryPayload {
  files: FileAssetRow[]
  usage_bytes: number
  capacity_bytes: number
}

function formatFileDate(iso: string): string {
  const date = new Date(iso)
  if (Number.isNaN(date.getTime())) return ''
  return date.toLocaleDateString(undefined, { dateStyle: 'medium' })
}

function formatFileSize(bytes: number): string {
  if (bytes === 0) return '0 B'
  const k = 1024
  const sizes = ['B', 'KB', 'MB', 'GB']
  const i = Math.floor(Math.log(bytes) / Math.log(k))
  return `${Math.round((bytes / k ** i) * 100) / 100} ${sizes[i]}`
}

/** Django returns MEDIA URLs — often `/media/...`. Prefix API_ORIGIN when UI is on another host; relative stays same-origin (dev proxy `/media`). */
function resolveBackendPublicUrl(raw: string | undefined): string {
  const url = (raw ?? '').trim()
  if (!url) return ''
  if (/^https?:\/\//i.test(url)) return url
  const origin = API_ORIGIN.replace(/\/$/, '')
  return origin ? `${origin}${url.startsWith('/') ? url : `/${url}`}` : url
}

const DEFAULT_CAPACITY_BYTES = 50 * 1024 * 1024

async function fetchMyFilesLibrary(): Promise<MyFilesLibraryPayload> {
  const empty: MyFilesLibraryPayload = { files: [], usage_bytes: 0, capacity_bytes: DEFAULT_CAPACITY_BYTES }
  const r = await authorizedFetch('/api/files/me/files')
  if (!r.ok) return empty
  const data = (await r.json()) as MyFilesLibraryPayload | FileAssetRow[]
  if (Array.isArray(data)) {
    return {
      files: data,
      usage_bytes: data.reduce((s, f) => s + (f.size_bytes ?? 0), 0),
      capacity_bytes: DEFAULT_CAPACITY_BYTES,
    }
  }
  return {
    files: Array.isArray(data.files) ? data.files : [],
    usage_bytes: typeof data.usage_bytes === 'number' ? data.usage_bytes : 0,
    capacity_bytes:
      typeof data.capacity_bytes === 'number' && data.capacity_bytes > 0
        ? data.capacity_bytes
        : DEFAULT_CAPACITY_BYTES,
  }
}

export function UserFilesPage() {
  const [uploading, setUploading] = useState(false)
  const [deletingFileId, setDeletingFileId] = useState<string | null>(null)
  const [pendingUploads, setPendingUploads] = useState<PendingUploadRow[]>([])
  const queryClient = useQueryClient()

  // The 'files' key is invalidated by the SSE content_update handler when the
  // agent's manage_files tool runs, so this list live-refreshes during chat.
  const { data: library, isLoading: loading } = useQuery({
    queryKey: ['files'],
    queryFn: fetchMyFilesLibrary,
  })
  const files = library?.files ?? []
  const usageBytes = library?.usage_bytes ?? 0
  const capacityBytes = library?.capacity_bytes ?? DEFAULT_CAPACITY_BYTES

  const refreshFiles = async () => {
    await queryClient.invalidateQueries({ queryKey: ['files'] })
  }

  const pendingUploadingBytes = pendingUploads
    .filter((row) => row.status === 'uploading')
    .reduce((s, row) => s + row.size_bytes, 0)
  const displayedUsageBytes = usageBytes + pendingUploadingBytes
  const capacityForBar = capacityBytes > 0 ? capacityBytes : 1
  const usagePercentFull =
    capacityBytes > 0 ? Math.min(100, (displayedUsageBytes / capacityForBar) * 100) : 0
  const usagePercentRounded = Math.min(100, Math.round(usagePercentFull))
  const libraryAtCapacity =
    capacityBytes > 0 && usageBytes + pendingUploadingBytes >= capacityBytes

  async function parseErrorBody(r: Response): Promise<string> {
    const text = await r.text()
    try {
      const parsed = JSON.parse(text) as { error?: string }
      if (parsed?.error) return parsed.error
    } catch {
      /* ignore */
    }
    return text || 'Request failed'
  }

  const uploadFiles = async (selectedFiles: File[]) => {
    if (!selectedFiles.length) return
    if (capacityBytes > 0) {
      const incoming = selectedFiles.reduce((s, f) => s + f.size, 0)
      if (usageBytes + incoming > capacityBytes) {
        console.error('Upload would exceed library storage limit')
        return
      }
    }
    const queuedAt = Date.now()
    const queuedUploads = selectedFiles.map((file, index) => ({
      id: `${queuedAt}-${index}-${file.name}`,
      original_name: file.name,
      size_bytes: file.size,
      status: 'uploading' as const,
    }))
    const successfulUploadIds = new Set<string>()

    setPendingUploads((current) => [...queuedUploads, ...current])
    setUploading(true)
    try {
      for (const [index, file] of selectedFiles.entries()) {
        const pendingRow = queuedUploads[index]
        const fd = new FormData()
        fd.append('file', file)
        const r = await authorizedFetch('/api/files/upload', { method: 'POST', body: fd })
        if (!r.ok) {
          const message = await parseErrorBody(r)
          console.error('Upload failed', message)
          setPendingUploads((current) =>
            current.map((row) =>
              row.id === pendingRow.id
                ? { ...row, status: 'failed', error: message || 'Upload failed' }
                : row
            )
          )
          continue
        }
        successfulUploadIds.add(pendingRow.id)
      }
      await refreshFiles()
      setPendingUploads((current) => current.filter((row) => !successfulUploadIds.has(row.id)))
    } finally {
      setUploading(false)
    }
  }

  const deleteFile = async (file: FileAssetRow) => {
    const confirmMessage =
      file.purpose === 'cv'
        ? `Remove "${file.original_name}" from Files? Your profile stays as it is.`
        : `Delete "${file.original_name}"?`
    if (!window.confirm(confirmMessage)) return

    setDeletingFileId(file.id)
    try {
      const r = await authorizedFetch(`/api/files/${file.id}`, { method: 'DELETE' })
      if (!r.ok) {
        console.error('Delete failed', await r.text())
        return
      }
      await refreshFiles()
    } finally {
      setDeletingFileId(null)
    }
  }

  const { title, description } = getPageHeader(
    '/files',
    'My files',
    'Personal documents. Your CV from onboarding appears here.'
  )
  const hasFileRows = pendingUploads.length > 0 || files.length > 0
  const hasOwnCvFile = files.some((file) => file.purpose === 'cv')
  const ownCv = useOwnCvReplace()
  const capacityMb = Math.max(1, Math.round(capacityBytes / (1024 * 1024)))
  const perFileMb = Math.max(1, Math.round(PER_FILE_MAX_BYTES / (1024 * 1024)))
  /** Matches `CardDescription` visibility in `card.tsx` (packs can hide default subtitles unless `alwaysShow`). */
  const showFilesCardSubtitle = !getPluginSlots().hideCardDescription

  return (
    <PageContainer>
      <PageHeader leading={<PageHeaderLeading />} title={title} description={description} icon={<FileText className="h-5 w-5" />} />
        <Card>
          {showFilesCardSubtitle ? (
            <CardHeader>
              <CardDescription>
                Personal documents, including your CV if you uploaded one during setup. Up to {capacityMb} MB
                total, {perFileMb} MB per file.
              </CardDescription>
            </CardHeader>
          ) : null}
          <CardContent className="space-y-4">
            {ownCv.isCandidate ? (
              <OwnCvFileInput
                inputRef={ownCv.inputRef}
                accept={ownCv.accept}
                onFiles={(files) => void ownCv.handleFiles(files)}
              />
            ) : null}
            <div className="space-y-2">
              <h2 className="text-sm font-semibold text-foreground">Files</h2>
              <Progress value={usagePercentFull} className="bg-secondary" />
              <p className="text-xs text-muted-foreground">
                {usagePercentRounded}% of library capacity used
              </p>
            </div>
            <FileUpload
              multiple
              maxSize={PER_FILE_MAX_BYTES}
              onFilesSelected={(selectedFiles) => void uploadFiles(selectedFiles)}
              disabled={uploading || libraryAtCapacity}
              showSelectedList={false}
              showSelectButton={false}
              title="Click to upload or drag and drop"
              activeTitle="Drop files here"
              description={`Documents, images, PDFs, and text files up to ${perFileMb} MB each`}
              dropzoneCardClassName="border-2 border-dashed bg-muted/30 shadow-none hover:bg-primary/5"
              dropzoneClassName="min-h-[180px] p-8"
            />
            {ownCv.isCandidate ? (
              <OwnCvStatusPanel
                primary={ownCv.primary}
                isProcessing={ownCv.isProcessing}
                isFailed={ownCv.isFailed}
                error={ownCv.error}
                busy={ownCv.busy}
                isRetrying={ownCv.isRetrying}
                hasOwnCvFile={hasOwnCvFile}
                uploading={ownCv.uploading}
                onPickFile={ownCv.pickFile}
                onRetry={() => void ownCv.handleRetry()}
              />
            ) : null}
            {loading && pendingUploads.length === 0 ? (
              <FileListSkeleton count={4} ariaLabel="Loading files" />
            ) : !hasFileRows ? (
              <div className="py-8 text-center text-muted-foreground">
                No files yet. Your CV from onboarding will show here, or upload a document to get started.
              </div>
            ) : (
              <ul>
                {pendingUploads.map((f) => (
                  <li key={f.id} className="flex flex-col gap-1 py-3 sm:flex-row sm:items-center sm:justify-between">
                    <div className="min-w-0 flex-1">
                      <div className="truncate text-sm font-medium" title={f.original_name}>
                        {f.original_name}
                      </div>
                      <div className="text-xs text-muted-foreground">
                        {formatFileSize(f.size_bytes)}
                        {' · '}
                        {f.status}
                        {f.error ? ` · ${f.error}` : ''}
                      </div>
                    </div>
                    {f.status === 'uploading' ? (
                      <Button
                        type="button"
                        variant="ghost"
                        size="iconSm"
                        className="shrink-0 self-start text-muted-foreground sm:self-center"
                        disabled
                        aria-label={`Uploading ${f.original_name}`}
                        title={`Uploading ${f.original_name}`}
                      >
                        <Loader2 className="h-4 w-4 animate-spin" aria-hidden />
                      </Button>
                    ) : (
                      <Button
                        type="button"
                        variant="ghost"
                        size="iconSm"
                        className="shrink-0 self-start text-muted-foreground hover:text-destructive sm:self-center"
                        onClick={() => setPendingUploads((current) => current.filter((row) => row.id !== f.id))}
                        aria-label={`Remove failed upload ${f.original_name}`}
                        title={`Remove failed upload ${f.original_name}`}
                      >
                        <Trash2 className="h-4 w-4" aria-hidden />
                      </Button>
                    )}
                  </li>
                ))}
                {files.map((f) => (
                  <li key={f.id} className="flex flex-col gap-1 py-3 sm:flex-row sm:items-center sm:justify-between">
                    <div className="min-w-0 flex-1">
                      <div className="flex min-w-0 flex-wrap items-center gap-2">
                        {f.file_url ? (
                          <a
                            href={resolveBackendPublicUrl(f.file_url)}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="block truncate text-sm font-medium underline-offset-4 hover:underline"
                            title={f.original_name}
                          >
                            {f.original_name}
                          </a>
                        ) : (
                          <div className="truncate text-sm font-medium" title={f.original_name}>
                            {f.original_name}
                          </div>
                        )}
                        {f.purpose === 'cv' ? (
                          <Badge variant="secondarySoft">Your CV</Badge>
                        ) : null}
                      </div>
                      <div className="text-xs text-muted-foreground">
                        {formatFileSize(f.size_bytes)}
                        {f.created_at ? (
                          <>
                            {' · '}
                            Uploaded {formatFileDate(f.created_at)}
                          </>
                        ) : null}
                        {f.mime_type ? ` · ${f.mime_type}` : ''}
                      </div>
                    </div>
                    <div className="flex shrink-0 items-center gap-2 self-start sm:self-center">
                      {f.purpose === 'cv' && ownCv.isCandidate ? (
                        <Button
                          type="button"
                          variant="outline"
                          size="sm"
                          onClick={ownCv.pickFile}
                          disabled={ownCv.busy || ownCv.isProcessing || deletingFileId === f.id}
                        >
                          {ownCv.uploading ? 'Uploading…' : 'Replace'}
                        </Button>
                      ) : null}
                    <Button
                      type="button"
                      variant="ghost"
                      size="iconSm"
                      className="shrink-0 text-muted-foreground hover:text-destructive"
                      onClick={() => void deleteFile(f)}
                      disabled={deletingFileId === f.id}
                      aria-label={`Delete ${f.original_name}`}
                      title={`Delete ${f.original_name}`}
                    >
                      {deletingFileId === f.id ? (
                        <Loader2 className="h-4 w-4 animate-spin" aria-hidden />
                      ) : (
                        <Trash2 className="h-4 w-4" aria-hidden />
                      )}
                    </Button>
                    </div>
                  </li>
                ))}
              </ul>
            )}
          </CardContent>
        </Card>
      </PageContainer>
  )
}
